import { Address4, Address6 } from "ip-address";

import en from "@/locales/en.json";
import ja from "@/locales/ja.json";

import { getLocale } from "./helper";

let locale = en;

let required = property => {
  locale = getLocale() === "en" ? en : ja;
  return v => !!v || `${property}${locale.validation.required_error_msg_txt}`;
};

let email = () => {
  locale = getLocale() === "en" ? en : ja;
  return v =>
    (v && /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v)) ||
    `${locale.validation.email_validation_error_msg_txt}`;
};

let intent = () => {
  return v => (v && /^[A-Za-z_]+$/.test(v)) || "Must be a valid Intent Name";
};

let alphaSpace = () => {
  return v => (v && /^[A-Za-z ]+$/.test(v)) || "Must be a valid Intent Name";
};

let alphaSpaceHyphen = () => {
  return v => (v && /^[-'a-zA-Z ]+$/.test(v)) || "Must be a valid Intent Name";
};

let minValue = (property, minValue) => {
  locale = getLocale() === "en" ? en : ja;
  return v => (v && parseFloat(v) >= minValue) || `${property} ${locale.validation.min_val_error_msg_txt} ${minValue}`;
};

let maxValue = (property, maxValue) => {
  return v => (v && parseFloat(v) <= maxValue) || `${property} must be less than ${maxValue}`;
};

let minLength = (property, minLength) => {
  return v =>
    (v && v.length >= minLength) ||
    `${property} ${locale.validation.min_val_error_msg_txt} ${minLength - 1} ${locale.validation.characters_validation_error_msg_txt}`;
};

let maxLength = (property, maxLength) => {
  return v =>
    (v && v.length <= maxLength) ||
    `${property} must be less than ${maxLength} ${locale.validation.characters_validation_error_msg_txt}`;
};

let match = property => {
  locale = getLocale() === "en" ? en : ja;
  return v => v === property || locale.validation.password_confirm_error_msg_txt;
};

let isNumeric = property => {
  locale = getLocale() === "en" ? en : ja;
  return v => (v && isNaN(v)) || `${property} ${locale.validation.numeric_error_msg_txt}`;
};

let password = property => {
  locale = getLocale() === "en" ? en : ja;
  return v =>
    (v && passwordCriteria(v)) ||
    `${locale.validation.password_valid_error_msg_txt_1} ${property} ${locale.validation.password_valid_error_msg_txt_2}`;
};

let passwordWithEmail = (property, fullEmail) => {
  locale = getLocale() === "en" ? en : ja;
  return v =>
    !v ||
    !fullEmail ||
    passwordEmailCheck(v, fullEmail) ||
    `${property} ${locale.validation.password_email_error_msg_txt}`;
};

let passwordWithName = (property, name, otherProperty) => {
  return v =>
    (v && passwordNameCheck(v, name)) || `${property} ${locale.validation.password_error_msg_txt} ${otherProperty}`;
};
let lowercase = property => {
  return v => (v && /^[a-z]+$/.test(v)) || `${property} must be in lowercase.`;
};

let ipAddress = errorMsg => v => {
  if (!v) return errorMsg || "Invalid IP address";

  // Reject IPv4 with leading zeros: 01.02.03.04, 192.168.01.1
  const hasLeadingZeros = /(?:^|\.)0\d/.test(v);
  if (hasLeadingZeros) return errorMsg || "Invalid IP address";

  const isValid = (() => {
    try {
      new Address4(v);
      return true;
    } catch {
      // not IPv4
    }
    try {
      new Address6(v);
      return true;
    } catch {
      // not IPv6
    }
    return false;
  })();

  return isValid || errorMsg || "Invalid IP address";
};

// checking password length and password criteria
const passwordCriteria = pw => {
  let criteria = 0;
  if (pw.toUpperCase() !== pw) {
    // has lower case letters
    criteria++;
  }
  if (pw.toLowerCase() !== pw) {
    // has upper case letters
    criteria++;
  }
  if (/^[a-zA-Z0-9]*$/.test(pw) === false) {
    // has special characters
    criteria++;
  }
  if (/\d/.test(pw) === true) {
    // has numbers
    criteria++;
  }
  // returns true if 3 or more criteria was met and length is appropriate
  return criteria >= 3 && pw.length >= 8;
};

// email username cheking with password -> is email username includes in user password
const passwordEmailCheck = (password, email, sliceLength = 3) => {
  if (!password || !email || typeof email != "string" || !email.includes("@")) return true;
  const [emailUserName] = email.split("@");
  if (emailUserName.length < sliceLength) return true;

  const checkEmail = [];
  for (let i = 0; i <= emailUserName.length - sliceLength; i++) {
    const substr = emailUserName.substr(i, sliceLength).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    checkEmail.push(substr);
  }

  const isCheckEmail = new RegExp(checkEmail.join("|"), "i").test(password);
  return !isCheckEmail;
};

// user Name and Display name is checking with password -> is user name and display name include in password?
const passwordNameCheck = (password, name) => {
  if (!password || !name || name.trim().length < 3) return true;
  const normalized = name.normalize("NFKC");
  let newName = normalized.replace(/[^\p{L}\p{N}]/gu, "").trim();
  if (newName.length < 3) return true;

  while (newName.length > 0 && newName.length < 5) {
    newName = newName.repeat(2);
  }
  let checkName = newName.match(/.{3}/g).concat(newName.substr(1).match(/.{3}/g), newName.substr(2).match(/.{3}/g));

  let isCheckName = new RegExp(checkName.join("|"), "i").test(password);
  return !isCheckName;
};

const requiredTrim = property => {
  locale = getLocale() === "en" ? en : ja;

  return v => {
    if (!v || !v.toString().trim()) {
      return `${property}${locale.validation.required_error_msg_txt}`;
    }
    return true;
  };
};

const phoneNumber = raw => {
  locale = getLocale() === "en" ? en : ja;
  if (!raw) return true;

  const value = raw.trim();

  const allowedCharsRegex = /^[+\d\-\s().]+$/;
  if (!allowedCharsRegex.test(value)) {
    return `${locale.validation.phone_invalid_chars}`;
  }

  const digits = value.replace(/\D/g, "");
  const len = digits.length;

  const MIN_DIGITS = 7;
  const MAX_DIGITS = 15;

  if (len < MIN_DIGITS || len > MAX_DIGITS) {
    return `${locale.validation.phone_invalid_length_error}`;
  }

  if (value.includes("+") && !/^\+/.test(value)) {
    return `${locale.validation.phone_plus_position_error}`;
  }

  return true;
};

const nonNegative = property => {
  const locale = getLocale() === "en" ? en : ja;

  return v => {
    const value = String(v ?? "").trim();
    if (!value) return true;

    return Number(value) >= 0 || `${property} ${locale.subscription_plans.validation.non_negative}`;
  };
};

const requiredTrimmed = property => {
  const locale = getLocale() === "en" ? en : ja;

  return v => !!String(v ?? "").trim() || `${property}${locale.validation.required_error_msg_txt}`;
};

const isEmpty = value => value === null || value === undefined || String(value).trim() === '';

export default {
  required,
  email,
  minValue,
  maxValue,
  minLength,
  maxLength,
  intent,
  match,
  password,
  passwordWithEmail,
  passwordWithName,
  lowercase,
  isNumeric,
  alphaSpace,
  alphaSpaceHyphen,
  ipAddress,
  requiredTrim,
  phoneNumber,
  nonNegative,
  requiredTrimmed,
  isEmpty
};
